import React from "react"

import authentication from "@irati/auth"
import styled from "styled-components"
import {H3} from "@irati/ui/src/Typography/Heading"
import {Hr} from "@irati/ui/src/Layout"
import tracking from "@irati-tracking/recognition/recognition"
import getSharingOptions, {PRIVATE_OPTION, TEAMS} from "@irati/recognition/constants/share"
import CreateRecognitionMutation, {NewRecognition} from "@irati/recognition/apollo/mutation/Recognition/Create"
import {FormattedMessage} from "@irati/utils/lang"
import {CompanyValuesSelect} from "@irati-web/fields/companyValues"
import OccasionsSelect from "@irati-web/fields/occasions/OccasionsSelect"
import {Share} from "@irati-web/fields/poll"
import isShareableWithTeams from "@irati/recognition/utils/isShareableWithTeams"
import {Message} from "@irati-web/fields/recognition"
import UserSearch from "@irati/ui/src/UserSearch"
import getId from "@irati/utils/id"
import {injectIntl, InjectIntlProps} from "@irati/utils/lang/injectIntl"
import Form from "@irati/ui/src/Form"
import {Recognition} from "@irati/types"
import Spacing from "@irati/ui/src/Spacing"
import CTA from "../shared/CTA"

interface Props extends InjectIntlProps {
  selectScreen: (modalToShow?: string) => void
  onRecognitionChange: (recognition: Recognition) => void
  onRecognitionCreated: (recognition: Recognition) => void
  recognition: NewRecognition
}
const StyledForm = styled(Form)`
  textarea {
    resize: vertical;
    min-height: 40px;
  }

  .Select-control > *:last-child {
    padding-right: 10px;
  }
`
const CreateRecognition: React.FC<Props> = (props) => {
  const {selectScreen, formatMessage, onRecognitionChange, onRecognitionCreated, recognition} = props

  const canShareWithEveryone = authentication.apps("recognition.recognition.everyone")
  const user = authentication.user()
  const canBeSharedWithTeams = isShareableWithTeams(user, recognition)
  const sharingOptions = getSharingOptions(formatMessage, canShareWithEveryone, canBeSharedWithTeams)

  const handleCancel = () => {
    tracking.cancelRecognition()
    selectScreen()
  }

  const handleRecognitionCreationComplete = (mutationRes: {createRecognition: Recognition}) => {
    tracking.launchRecognition(recognition)
    onRecognitionCreated(mutationRes.createRecognition)
  }

  const handleOccasionsChange = (value: string) => {
    onRecognitionChange({
      ...recognition,
      occasionId: value
    })
  }

  const handleRecognitionError = (response: any) => {
    tracking.errorsRecognition(response)
  }

  const handleUserChange = (_, users) => {
    onRecognitionChange({
      ...recognition,
      userIds: users || []
    })
  }

  const onChange = (editedRecognition: Recognition, key: string, value: any) => {
    let newRecognition = {
      ...editedRecognition,
      [key]: value
    }

    const newRecCanBeSharedWithTeams = isShareableWithTeams(user, newRecognition)

    if (!newRecCanBeSharedWithTeams && newRecognition.share.value === TEAMS) {
      newRecognition = {
        ...newRecognition,
        share: PRIVATE_OPTION(formatMessage)
      }
    }
    onRecognitionChange(newRecognition)
  }
  return (
    <Spacing>
      <H3>
        <FormattedMessage id="recognition.popup.createRecognition.create.header" />
      </H3>
      <Hr />
      <StyledForm>
        <UserSearch
          hideLabel
          onChange={handleUserChange}
          removeSelf
          isMulti
          autoFocus
          label="recognition.fields.recognition.winners.label"
          placeholder="recognition.fields.recognition.winners.placeholder"
        />
        <Message
          hideLabel
          label={formatMessage({id: "recognition.popup.createRecognition.create.message.label"})}
          placeholder={formatMessage({id: "recognition.popup.createRecognition.create.message.placeholder"})}
          poll={recognition}
          onChange={onChange}
        />
        <OccasionsSelect selectedOption={recognition.occasionId} onChange={handleOccasionsChange} />
        <CompanyValuesSelect item={recognition} onChange={onChange} />
        <Share options={sharingOptions} poll={recognition} onChange={onChange} hasTeams={canBeSharedWithTeams} />
      </StyledForm>
      <CreateRecognitionMutation
        onCompleted={handleRecognitionCreationComplete}
        onError={handleRecognitionError}
        recognition={recognition}>
        {(onClick, apolloState) => (
          <CTA
            id={getId("submitButton", props)}
            onNext={onClick}
            onCancel={handleCancel}
            apolloState={apolloState}
            nextText={formatMessage({id: "recognition.popup.createRecognition.create.cta"})}
          />
        )}
      </CreateRecognitionMutation>
    </Spacing>
  )
}
export default injectIntl(CreateRecognition)
